// Typed fetch wrapper: same-origin, cookies included, CSRF header on every call, JSON in/out.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;
  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

type HeaderHook = () => Record<string, string>;
const headerHooks: HeaderHook[] = [
  // Harney Identity and our Pages Functions reject state-changing calls without it.
  () => ({ 'X-Requested-With': 'XMLHttpRequest' }),
];

/** Register extra request headers (for example a CSRF token once the backend issues one). */
export function addHeaderHook(hook: HeaderHook): () => void {
  headerHooks.push(hook);
  return () => {
    const i = headerHooks.indexOf(hook);
    if (i >= 0) headerHooks.splice(i, 1);
  };
}

export type Query = Record<string, string | number | boolean | readonly string[] | null | undefined>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === '') continue;
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  for (const hook of headerHooks) Object.assign(headers, hook());
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(withQuery(path, opts.query), {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Network error: the server could not be reached.');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const code =
      data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : `http_${res.status}`;
    throw new ApiError(res.status, code, `Request failed (${res.status} ${code})`, data);
  }
  return data as T; // typed boundary: callers name the response type, the server owns the shape
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
};
